import { cn } from '@/lib/utils'
import { InfoPopover } from '@/components/InfoPopover'
import { MetricDeltas } from './MetricDeltas'
import { MONO, fmt } from './format'

interface StatPillProps {
  label: string
  value: number
  /** Prior month's value, or null if not in the dataset. */
  prevMonth: number | null
  /** Same month last year's value, or null if not in the dataset. */
  prevYear: number | null
  prevYearLabel: string
  currency: string
  /** Glossary key for the info popover. */
  term?: string
  /** Credit notes for the period (Revenue only) — shows a gross → net line when > 0. */
  creditNotes?: number
  /** Label for the prior comparison, e.g. "prior month" or "prior period". */
  prevLabel?: string
}

/** A supporting KPI tile with prior-period and year-over-year comparisons. */
export function StatPill({ label, value, prevMonth, prevYear, prevYearLabel, currency, term, creditNotes, prevLabel }: StatPillProps) {
  return (
    <div className="rounded-2xl border border-stone-200 bg-white p-4 dark:border-stone-800 dark:bg-stone-900">
      <p className="inline-flex items-center gap-1 text-xs font-medium text-stone-500 dark:text-stone-400">
        {label}
        {term && <InfoPopover term={term} />}
      </p>
      <p className={cn('mt-1 text-xl font-semibold tracking-tight', MONO)}>{fmt(value, currency)}</p>
      {creditNotes != null && creditNotes > 0 && (
        <p className="mt-1 text-[11px] leading-tight text-stone-400 dark:text-stone-500">
          <span className="text-red-600 dark:text-red-400">−{fmt(creditNotes, currency)}</span> CN ·{' '}
          <span className={cn('font-medium text-stone-600 dark:text-stone-300', MONO)}>{fmt(value - creditNotes, currency)}</span> net
        </p>
      )}
      <MetricDeltas
        current={value}
        prevMonth={prevMonth}
        prevYear={prevYear}
        prevYearLabel={prevYearLabel}
        prevLabel={prevLabel}
        currency={currency}
      />
    </div>
  )
}
